import React, { useState, useEffect, useRef } from 'react';
import { Negociacao, AnaliseCadastral, ConferenciaDocs, NegociacaoNota } from '../../../../types';
import { supabase } from '../../../../src/lib/supabase';
import { logUsageEvent } from '../../../../src/lib/logger';
import { useToast } from '../../../../src/contexts/ToastContext';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { getTermoValido } from '../../../../src/lib/termoValido';
import { Phone, Mail, User, Building, Landmark, Save, FileText, ExternalLink, Link2, ChevronDown, Zap, ClipboardCheck, UserCheck, AlertTriangle, Send, MessageSquare, StickyNote, Upload, Loader2, Trash2 } from 'lucide-react';

interface Step1Props {
    negociacao: Negociacao;
    onChange: (data: Partial<Negociacao>) => void;
    analiseData: Partial<AnaliseCadastral>;
    onAnaliseChange: (data: Partial<AnaliseCadastral>) => void;
    documentos_json?: { url: string; name: string; source?: string }[] | null;
    onValidate?: (isValid: boolean) => void;
}

export default function Step1Identificacao({ negociacao, onChange, analiseData, onAnaliseChange, documentos_json, onValidate }: Step1Props) {
    const { addToast } = useToast();
    const { user, profile } = useAuth();
    const [pendenciaMsg, setPendenciaMsg] = useState('');
    const [sendingPendencia, setSendingPendencia] = useState(false);
    const [formData, setFormData] = useState({
        cliente_nome: negociacao.cliente_nome || '',
        cpf_cnpj: negociacao.cpf_cnpj || '',
        whatsapp: negociacao.whatsapp || '',
        email: negociacao.email || '',
        tipo_cliente: negociacao.tipo_cliente || 'PF',
        nome_representante_legal: (negociacao as any).nome_representante_legal || '',
        // #6384: vazio fica vazio. O termo e quem lê o financeiro usam o dado principal
        // do cliente quando o campo não foi preenchido — copiar aqui duplicava o número.
        contato_financeiro: negociacao.contato_financeiro || '',
        email_financeiro: negociacao.email_financeiro || '',
        whatsapp_financeiro: negociacao.whatsapp_financeiro || ''
    });
    const [saving, setSaving] = useState(false);
    const [notas, setNotas] = useState<NegociacaoNota[]>([]);
    const [loadingNotas, setLoadingNotas] = useState(true);
    const [showNotas, setShowNotas] = useState(false);
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const isCondominio = analiseData.is_condominio || false;
    const docs = analiseData.conferencia_docs || {};

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        setUploadingFiles(true);
        try {
            const uploadedDocs = [...(documentos_json || [])];
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const fileExt = file.name.split('.').pop();
                const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

                const { error: uploadError } = await supabase.storage
                    .from('tarefas-anexos')
                    .upload(fileName, file);

                if (uploadError) throw uploadError;

                const { data: { publicUrl } } = supabase.storage
                    .from('tarefas-anexos')
                    .getPublicUrl(fileName);

                uploadedDocs.push({ url: publicUrl, name: file.name, source: 'analise' });
            }

            const { error: updateError } = await supabase
                .from('negociacoes')
                .update({ documentos_json: uploadedDocs })
                .eq('id', negociacao.id);

            if (updateError) throw updateError;

            onChange({ ...negociacao, documentos_json: uploadedDocs });
            addToast('Documentos anexados com sucesso!', 'success');
            logUsageEvent({ userId: user?.id, modulo: 'gestao', funcionalidade: 'dashboard', acao: 'anexar_documento_negociacao', origem: 'acao' });
        } catch (error: any) {
            console.error('Erro ao anexar arquivo:', error);
            addToast(`Erro ao anexar arquivo: ${error.message}`, 'error');
        } finally {
            setUploadingFiles(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0 && fileInputRef.current) {
            fileInputRef.current.files = e.dataTransfer.files;
            handleFileUpload({ target: fileInputRef.current } as any);
        }
    };

    const handleDeleteDocument = async (urlToDelete: string) => {
        if (!confirm('Tem certeza que deseja excluir este documento?')) return;

        try {
            const uploadedDocs = (documentos_json || []).filter((doc: any) => doc.url !== urlToDelete);

            const { error: updateError } = await supabase
                .from('negociacoes')
                .update({ documentos_json: uploadedDocs })
                .eq('id', negociacao.id);

            if (updateError) throw updateError;

            onChange({ ...negociacao, documentos_json: uploadedDocs });
            addToast('Documento excluído com sucesso!', 'success');
            logUsageEvent({ userId: user?.id, modulo: 'gestao', funcionalidade: 'dashboard', acao: 'excluir_documento_negociacao', origem: 'acao' });
        } catch (error: any) {
            console.error('Erro ao excluir documento:', error);
            addToast(`Erro ao excluir documento: ${error.message}`, 'error');
        }
    };

    // Fetch negotiation notes
    useEffect(() => {
        const fetchNotas = async () => {
            setLoadingNotas(true);
            try {
                const { data: notesData } = await supabase
                    .from('negociacao_notas')
                    .select('*')
                    .eq('negociacao_id', negociacao.id)
                    .order('created_at', { ascending: false })
                    .limit(10);

                if (notesData && notesData.length > 0) {
                    // Fetch user profiles for note authors
                    const userIds = [...new Set(notesData.map(n => n.user_id).filter(Boolean))];
                    let profilesMap: Record<string, any> = {};
                    if (userIds.length > 0) {
                        const { data: profiles } = await supabase
                            .from('profiles')
                            .select('id, full_name, avatar_url')
                            .in('id', userIds);
                        profiles?.forEach(p => profilesMap[p.id] = p);
                    }
                    // Attach user profile to each note
                    const enriched = notesData.map(n => ({
                        ...n,
                        user: profilesMap[n.user_id] || undefined
                    }));
                    setNotas(enriched);
                } else {
                    setNotas([]);
                }
            } catch (err) {
                console.error('Error fetching notas:', err);
                setNotas([]);
            }
            setLoadingNotas(false);
        };
        fetchNotas();
    }, [negociacao.id]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const updates = {
                whatsapp: formData.whatsapp,
                email: formData.email,
                nome_representante_legal: formData.nome_representante_legal,
                contato_financeiro: formData.contato_financeiro,
                email_financeiro: formData.email_financeiro,
                whatsapp_financeiro: formData.whatsapp_financeiro,
                updated_at: new Date().toISOString()
            };
            const { error } = await supabase.from('negociacoes').update(updates).eq('id', negociacao.id);
            if (error) throw error;
            onChange(updates);
            addToast('Dados salvos com sucesso', 'success');
            logUsageEvent({ userId: user?.id, modulo: 'gestao', funcionalidade: 'dashboard', acao: 'salvar_dados_cliente_analise', origem: 'acao' });
        } catch (error) {
            console.error('Error auto-saving:', error);
            addToast('Erro ao atualizar dados do cliente', 'error');
        } finally {
            setSaving(false);
        }
    };

    // ── Conferência Documental helpers ──
    const updateDoc = (key: keyof ConferenciaDocs, field: string, value: any) => {
        let updatedDoc = { ...(docs[key] as any), [field]: value };
        if (field === 'presenca' && value !== 'Completo') {
            updatedDoc.legivel = undefined;
            updatedDoc.atual = undefined;
            updatedDoc.titular_divergente = undefined;
        }
        onAnaliseChange({
            conferencia_docs: { ...docs, [key]: updatedDoc }
        });
    };

    const updateGD = (value: boolean) => {
        onAnaliseChange({ conferencia_docs: { ...docs, possui_gd: value } });
    };

    const updateTermo = (field: string, value: any) => {
        onAnaliseChange({
            conferencia_docs: {
                ...docs,
                termo_adesao: { ...(docs.termo_adesao || {}), [field]: value }
            }
        });
    };

    // ── Termo data ──
    const urlTermoArray = negociacao.url_termo;
    const ultimoTermo = getTermoValido(urlTermoArray);
    const numeroTermoFromUrl = ultimoTermo?.numero_termo || null;
    const termoNumero = numeroTermoFromUrl || (negociacao as any).id_termo_autentique || `TA-${new Date().getFullYear()}-${negociacao.id.toString().padStart(3, '0')}`;
    const termoLink = ultimoTermo?.url_pdf || null;
    const termoAssinadoEm = ultimoTermo?.data_assinatura || null;

    // ── Simulação anexada ao termo (Plano de Benefícios do Quadro Resumo) ──
    // Termos novos gravam a simulação escolhida na emissão; termos antigos caem na
    // simulação mais recente até a data do termo (comportamento da emissão antiga).
    const simulacaoDoTermo = (() => {
        if (ultimoTermo?.numero_simulacao || ultimoTermo?.link_simulacao) {
            return { numero: ultimoTermo.numero_simulacao || '', link: ultimoTermo.link_simulacao || '', data: ultimoTermo.data_simulacao || '' };
        }
        const sims = negociacao.url_simulacao || [];
        if (!ultimoTermo || sims.length === 0) return null;
        const anteriores = sims.filter(s => s.data_criacao && s.data_criacao <= ultimoTermo.data_criacao);
        const candidatas = anteriores.length > 0 ? anteriores : sims;
        const sim = candidatas[candidatas.length - 1];
        return { numero: sim.numero_simulacao || '', link: sim.url_pdf || '', data: sim.data_criacao || '' };
    })();
    const simulacaoDataStr = simulacaoDoTermo?.data && !isNaN(new Date(simulacaoDoTermo.data).getTime())
        ? new Date(simulacaoDoTermo.data).toLocaleDateString('pt-BR')
        : '';

    const inputClass = "w-full flex h-10 rounded-xl border border-slate-200 dark:border-slate-700/50 bg-white dark:bg-slate-800/40 px-3.5 py-2 text-sm text-slate-900 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 font-medium shadow-sm hover:border-slate-300 dark:hover:border-slate-600";
    const disabledInputClass = `${inputClass} bg-slate-100 dark:bg-slate-800/70 cursor-not-allowed hover:border-slate-200 dark:hover:border-slate-700/50`;

    // ── Conferência render helpers ──
    const renderPresencaOptions = (docKey: keyof ConferenciaDocs, showNA: boolean = false) => {
        const value = (docs[docKey] as any)?.presenca;
        return (
            <div className="flex flex-wrap gap-4 items-center">
                {showNA && (
                    <label className="inline-flex items-center gap-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800 p-1.5 rounded transition-colors">
                        <input type="radio" name={`${docKey}_presenca`} checked={value === 'N/A'} onChange={() => updateDoc(docKey, 'presenca', value === 'N/A' ? undefined : 'N/A')} className="w-3.5 h-3.5 rounded-full text-primary focus:ring-primary border-slate-700 bg-slate-800/60" />
                        <span className="text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-widest text-[10px]">N/A</span>
                    </label>
                )}
                <label className="inline-flex items-center gap-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800 p-1.5 rounded transition-colors">
                    <input type="radio" name={`${docKey}_presenca`} checked={value === 'Completo'} onChange={() => updateDoc(docKey, 'presenca', value === 'Completo' ? undefined : 'Completo')} className="w-3.5 h-3.5 rounded-full text-primary focus:ring-primary border-slate-700 bg-slate-800/60" />
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-widest text-[10px]">Completo</span>
                </label>
                <label className="inline-flex items-center gap-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800 p-1.5 rounded transition-colors">
                    <input type="radio" name={`${docKey}_presenca`} checked={value === 'Incomp.' || value === 'Incompleto'} onChange={() => updateDoc(docKey, 'presenca', value === 'Incomp.' || value === 'Incompleto' ? undefined : (showNA ? 'Incomp.' : 'Incompleto'))} className="w-3.5 h-3.5 rounded-full text-primary focus:ring-primary border-slate-700 bg-slate-800/60" />
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-widest text-[10px]">{showNA ? 'Incomp.' : 'Incompleto'}</span>
                </label>
            </div>
        );
    };

    const renderLegibilidadeOptions = (docKey: keyof ConferenciaDocs) => {
        const value = (docs[docKey] as any)?.legivel;
        return (
            <div className="flex gap-4 items-center animate-in fade-in zoom-in duration-300">
                <span className="text-[10px] font-bold text-slate-400/80 mr-2 uppercase tracking-wide">Legível?</span>
                <label className="inline-flex items-center gap-1.5 cursor-pointer hover:bg-green-50 dark:hover:bg-green-900/20 p-1 rounded transition-colors">
                    <input type="radio" name={`${docKey}_legivel`} checked={value === true} onChange={() => updateDoc(docKey, 'legivel', value === true ? undefined : true)} className="w-3.5 h-3.5 text-green-500 focus:ring-green-500 border-slate-700 bg-slate-800/60" />
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-200">Sim</span>
                </label>
                <label className="inline-flex items-center gap-1.5 cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/20 p-1 rounded transition-colors">
                    <input type="radio" name={`${docKey}_legivel`} checked={value === false} onChange={() => updateDoc(docKey, 'legivel', value === false ? undefined : false)} className="w-3.5 h-3.5 text-red-500 focus:ring-red-500 border-slate-700 bg-slate-800/60" />
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-200">Não</span>
                </label>
            </div>
        );
    };

    const renderAtualOptions = (docKey: keyof ConferenciaDocs) => {
        const value = (docs[docKey] as any)?.atual;
        return (
            <div className="flex gap-4 items-center animate-in fade-in zoom-in duration-300 border-l border-gray-200 dark:border-slate-700 pl-4">
                <span className="text-[10px] font-bold text-slate-400/80 mr-2 uppercase tracking-wide">Atual:</span>
                <label className="inline-flex items-center gap-1.5 cursor-pointer hover:bg-green-50 dark:hover:bg-green-900/20 p-1 rounded transition-colors">
                    <input type="radio" name={`${docKey}_atual`} checked={value === true} onChange={() => updateDoc(docKey, 'atual', value === true ? undefined : true)} className="w-3.5 h-3.5 text-green-500 focus:ring-green-500 border-slate-700 bg-slate-800/60" />
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-200">Sim</span>
                </label>
                <label className="inline-flex items-center gap-1.5 cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/20 p-1 rounded transition-colors">
                    <input type="radio" name={`${docKey}_atual`} checked={value === false} onChange={() => updateDoc(docKey, 'atual', value === false ? undefined : false)} className="w-3.5 h-3.5 text-red-500 focus:ring-red-500 border-slate-700 bg-slate-800/60" />
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-200">Não</span>
                </label>
            </div>
        );
    };

    const renderDivergenteOption = (docKey: keyof ConferenciaDocs) => {
        const value = (docs[docKey] as any)?.titular_divergente;
        return (
            <div className="animate-in fade-in zoom-in duration-300">
                <label className="inline-flex items-center gap-1.5 cursor-pointer text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 p-1.5 rounded transition-colors">
                    <input type="checkbox" checked={value === true} onChange={() => updateDoc(docKey, 'titular_divergente', !value)} className="w-3.5 h-3.5 rounded text-amber-500 focus:ring-amber-500 border-amber-300" />
                    <span className="text-xs font-medium">Titular Divergente</span>
                </label>
            </div>
        );
    };

    const renderTermoOption = (field: keyof NonNullable<ConferenciaDocs['termo_adesao']>, label: string) => {
        const value = docs.termo_adesao?.[field];
        return (
            <div className="flex flex-col gap-2 p-3 bg-slate-100 dark:bg-slate-800 rounded-md border border-slate-200 dark:border-slate-700 shadow-sm w-48">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{label}</span>
                <div className="flex gap-4">
                    <label className="inline-flex items-center gap-1.5 cursor-pointer hover:bg-green-50 dark:hover:bg-green-900/20 p-1 rounded transition-colors">
                        <input type="radio" checked={value === true} onChange={() => updateTermo(field, value === true ? undefined : true)} className="w-3.5 h-3.5 rounded-full text-green-500 focus:ring-green-500 border-slate-700 bg-slate-800/60" />
                        <span className="text-xs font-bold text-slate-700 dark:text-slate-200">Sim</span>
                    </label>
                    <label className="inline-flex items-center gap-1.5 cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/20 p-1 rounded transition-colors">
                        <input type="radio" checked={value === false} onChange={() => updateTermo(field, value === false ? undefined : false)} className="w-3.5 h-3.5 rounded-full text-red-500 focus:ring-red-500 border-slate-700 bg-slate-800/60" />
                        <span className="text-xs font-bold text-slate-700 dark:text-slate-200">Não</span>
                    </label>
                </div>
            </div>
        );
    };

    const isDocComplete = (docKey: keyof ConferenciaDocs) => (docs[docKey] as any)?.presenca === 'Completo';
    const hasDocAnyStatus = (docKey: keyof ConferenciaDocs) => !!(docs[docKey] as any)?.presenca;

    const renderDocRow = (title: string, docKey: keyof ConferenciaDocs, showNA = false, hasAtual = false, hasDivergente = false, isOnlyStatus = false, customContent: React.ReactNode = null, extraContent: React.ReactNode = null) => {
        const isActive = isOnlyStatus ? true : isDocComplete(docKey);
        const hasInteraction = isOnlyStatus ? !!(docs[docKey] as any)?.status : hasDocAnyStatus(docKey);
        return (
            <div key={docKey} className={`p-5 rounded-2xl border transition-all duration-300 ${hasInteraction ? 'border-primary/30 bg-primary/5 dark:bg-primary/10 shadow-md shadow-primary/5' : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 hover:border-slate-300 dark:hover:border-slate-700 shadow-sm'}`}>
                <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
                    <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 md:min-w-[300px]">
                        <div className="font-bold text-sm text-slate-800 dark:text-slate-200 w-full md:w-56">{title}</div>
                        {isOnlyStatus && customContent ? customContent : renderPresencaOptions(docKey, showNA)}
                    </div>
                    {isActive && !isOnlyStatus && (
                        <div className="flex flex-wrap items-center gap-4 xl:gap-6 mt-3 xl:mt-0">
                            <div className="flex flex-wrap gap-2 sm:gap-4 bg-white dark:bg-slate-800/80 p-1.5 rounded-xl border border-slate-200 dark:border-slate-700/80 shadow-sm">
                                {renderLegibilidadeOptions(docKey)}
                                {hasAtual && renderAtualOptions(docKey)}
                            </div>
                            {hasDivergente && renderDivergenteOption(docKey)}
                        </div>
                    )}
                    {isOnlyStatus && isActive && hasDivergente && (
                        <div className="flex items-center gap-6 mt-3 xl:mt-0">
                            {renderDivergenteOption(docKey)}
                        </div>
                    )}
                </div>
                {extraContent && (
                    <div className="mt-4 pt-4 border-t border-slate-200/80 dark:border-slate-800 animate-in fade-in slide-in-from-top-2 duration-300">
                        {extraContent}
                    </div>
                )}
            </div>
        );
    };

    const renderCartaoCNPJ = () => (
        <div className={`p-5 rounded-2xl border transition-all duration-300 ${docs.cartao_cnpj?.apresentado !== undefined ? 'border-primary/30 bg-primary/5 dark:bg-primary/10 shadow-md shadow-primary/5' : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 hover:border-slate-300 dark:hover:border-slate-700 shadow-sm'}`}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 md:min-w-[300px]">
                    <div className="font-bold text-sm text-slate-800 dark:text-slate-200 md:w-56 w-full">Cartão CNPJ</div>
                    <div className="flex flex-wrap gap-4 items-center">
                        <span className="text-[10px] font-bold text-slate-400 mr-2 uppercase tracking-wide">Apresentado?</span>
                        <label className="inline-flex items-center gap-1.5 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-800 p-1.5 rounded-lg transition-colors">
                            <input type="radio" checked={docs.cartao_cnpj?.apresentado === true} onChange={() => updateDoc('cartao_cnpj', 'apresentado', docs.cartao_cnpj?.apresentado === true ? undefined : true)} className="w-4 h-4 rounded-full text-primary focus:ring-primary border-slate-700 bg-slate-800/60" />
                            <span className="text-xs font-bold text-slate-700 dark:text-slate-200">Sim</span>
                        </label>
                        <label className="inline-flex items-center gap-1.5 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-800 p-1.5 rounded-lg transition-colors">
                            <input type="radio" checked={docs.cartao_cnpj?.apresentado === false} onChange={() => {
                                const isFalse = docs.cartao_cnpj?.apresentado === false;
                                onAnaliseChange({ conferencia_docs: { ...docs, cartao_cnpj: { ...(docs.cartao_cnpj as any), apresentado: isFalse ? undefined : false, status: undefined } } });
                            }} className="w-4 h-4 rounded-full text-primary focus:ring-primary border-slate-700 bg-slate-800/60" />
                            <span className="text-xs font-bold text-slate-700 dark:text-slate-200">Não</span>
                        </label>
                    </div>
                </div>
                {docs.cartao_cnpj?.apresentado === true && (
                    <div className="flex flex-wrap gap-4 items-center bg-white dark:bg-slate-800 p-2.5 px-5 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm animate-in fade-in zoom-in duration-300 mt-3 md:mt-0">
                        <span className="text-[10px] font-bold text-slate-400 mr-2 uppercase tracking-wide">Status:</span>
                        <label className="inline-flex items-center gap-1.5 cursor-pointer hover:bg-green-50 dark:hover:bg-green-900/20 p-1 rounded transition-colors">
                            <input type="radio" checked={docs.cartao_cnpj?.status === 'Ativa'} onChange={() => updateDoc('cartao_cnpj', 'status', docs.cartao_cnpj?.status === 'Ativa' ? undefined : 'Ativa')} className="w-4 h-4 text-green-500 focus:ring-green-500 border-slate-700 bg-slate-800/60" />
                            <span className="text-xs text-slate-700 dark:text-slate-200 font-bold">Ativa</span>
                        </label>
                        <label className="inline-flex items-center gap-1.5 cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/20 p-1 rounded transition-colors">
                            <input type="radio" checked={docs.cartao_cnpj?.status === 'Inativa'} onChange={() => updateDoc('cartao_cnpj', 'status', docs.cartao_cnpj?.status === 'Inativa' ? undefined : 'Inativa')} className="w-4 h-4 text-red-500 focus:ring-red-500 border-slate-700 bg-slate-800/60" />
                            <span className="text-xs text-slate-700 dark:text-slate-200 font-bold">Inativa</span>
                        </label>
                    </div>
                )}
            </div>
        </div>
    );

    // ── Document Checklists by type ──
    const renderExtraFields = (docKey: keyof ConferenciaDocs) => {
        const doc = docs[docKey] as any;
        if (!doc || (doc.presenca !== 'Completo' && !doc.status)) return null;

        const updateField = (field: string, value: any) => updateDoc(docKey, field, value);
        const fieldInput = (field: string, label: string, placeholder: string, type: string = 'text') => (
            <div className="flex flex-col gap-1.5 w-full">
                <label className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">{label}</label>
                <input type={type} value={doc[field] || ''} onChange={(e) => updateField(field, e.target.value)} className="h-10 w-full px-3 rounded-xl border border-slate-300 dark:border-slate-700/80 bg-slate-50/50 dark:bg-slate-900/50 text-sm font-semibold text-slate-900 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:bg-white dark:focus:bg-slate-800 transition-all placeholder:text-slate-400 shadow-sm" placeholder={placeholder} />
            </div>
        );
        const boolToggle = (field: string, label: string) => {
            const val = doc[field];
            return (
                <div className="flex items-center justify-between bg-slate-50/50 dark:bg-slate-900/50 p-3 px-4 rounded-xl border border-slate-200 dark:border-slate-700/80 shadow-sm w-full">
                    <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">{label}</span>
                    <div className="flex items-center gap-4">
                        <label className="inline-flex items-center gap-1.5 cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/30 px-2 py-1.5 rounded-lg transition-colors"><input type="radio" checked={val === true} onChange={() => updateField(field, val === true ? undefined : true)} className="w-4 h-4 text-green-500 focus:ring-green-500 bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600" /><span className="text-xs font-bold text-slate-800 dark:text-slate-200">Sim</span></label>
                        <label className="inline-flex items-center gap-1.5 cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/30 px-2 py-1.5 rounded-lg transition-colors"><input type="radio" checked={val === false} onChange={() => updateField(field, val === false ? undefined : false)} className="w-4 h-4 text-red-500 focus:ring-red-500 bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600" /><span className="text-xs font-bold text-slate-800 dark:text-slate-200">Não</span></label>
                    </div>
                </div>
            );
        };

        switch (docKey) {
            case 'rg_cnh':
                return <div className="max-w-xs">{boolToggle('ativo', 'Documento Ativo?')}</div>;
            case 'contrato_social':
                return (
                    <div className="space-y-4">
                        <div className="max-w-xs">{boolToggle('cnpj_ativo', 'CNPJ Ativo?')}</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {fieldInput('nome_socio_administrador', 'Nome Sócio Admin.', 'Nome completo')}
                            {fieldInput('cpf_socio_administrador', 'CPF Sócio Admin.', '000.000.000-00')}
                        </div>
                    </div>
                );
            case 'ata_eleicao':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {fieldInput('nome_representante', 'Nome Representante', 'Nome completo')}
                        {fieldInput('cpf_representante', 'CPF Representante', '000.000.000-00')}
                        {fieldInput('validade_ata', 'Validade da Ata', '', 'date')}
                    </div>
                );
            case 'ata_constituicao':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {fieldInput('data_constituicao', 'Data Constituição', '', 'date')}
                        {fieldInput('cnpj_condominio_str', 'CNPJ do Condomínio', '00.000.000/0000-00')}
                    </div>
                );
            case 'procuracao':
                return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {fieldInput('nome_procurador', 'Nome Procurador', 'Nome completo')}
                        {fieldInput('cpf_procurador', 'CPF Procurador', '000.000.000-00')}
                        {fieldInput('validade_procuracao', 'Validade', '', 'date')}
                        {fieldInput('poderes_especificos', 'Poderes', 'Gerais / Específicos')}
                    </div>
                );
            case 'contrato_locacao':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {fieldInput('data_inicio', 'Data Início', '', 'date')}
                        {fieldInput('data_fim', 'Data Fim', '', 'date')}
                        {fieldInput('locatario_nome', 'Locatário', 'Nome do locatário')}
                    </div>
                );
            case 'contrato_compra_venda':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {fieldInput('data_escritura', 'Data Escritura', '', 'date')}
                        {fieldInput('comprador_nome', 'Comprador', 'Nome do comprador')}
                    </div>
                );
            case 'fatura_energia':
                return null;
            default:
                return null;
        }
    };

    const renderDocRowExpanded = (title: string, docKey: keyof ConferenciaDocs, showNA = false, hasAtual = false, hasDivergente = false, isOnlyStatus = false, customContent: React.ReactNode = null) => {
        return renderDocRow(title, docKey, showNA, hasAtual, hasDivergente, isOnlyStatus, customContent, renderExtraFields(docKey));
    };

    const renderPFChecklist = () => (
        <div className="space-y-3">
            {renderDocRowExpanded("RG + CPF (Frente e Verso)", "rg_cnh")}
            {renderDocRowExpanded("Procuração (se aplicável)", "procuracao", true, true, true)}
        </div>
    );

    const renderFaturaEnergiaExpanded = () => null;

    const renderPJChecklist = () => (
        <div className="space-y-3">
            {renderDocRowExpanded("Contrato Social + Última Alteração", "contrato_social")}
            {renderDocRowExpanded("Documento do Representante Legal", "rg_cnh")}
            {renderCartaoCNPJ()}
        </div>
    );

    const renderCondominioChecklist = () => (
        <div className="space-y-3">
            {renderDocRowExpanded("Estatuto / Ata de Constituição", "ata_constituicao")}
            {renderDocRowExpanded("Ata de Eleição do Representante", "ata_eleicao", false, true, true)}
            {renderDocRowExpanded("Documentos do Representante", "rg_cnh")}
            {renderCartaoCNPJ()}
        </div>
    );

    // Validation logic for the whole step
    const isValid = React.useMemo(() => {
        // 1. Identificação Geral Form
        if (!formData.whatsapp || !formData.email) return false;
        if (formData.tipo_cliente === 'PJ' && !formData.nome_representante_legal) return false;

        // 2. Termo de Adesão
        const termoFieldsKeys = [
            'endereco_rua', 'endereco_numero', 'endereco_quadra_lote', 'endereco_bairro', 'endereco_cidade', 'endereco_cep',
            'assinante_titular_poderes', 'dados_associado_corretos',
            'desconto_fidelidade_corretos', 'ucs_conferem_faturas', 'todas_ucs_titular',
            'termo_assinado', 'autenticidade_assinaturas', 'desconto_concedido_certo',
            'quantidade_unidades_certa'
        ];
        if (termoFieldsKeys.some(f => docs.termo_adesao?.[f as keyof NonNullable<ConferenciaDocs['termo_adesao']>] === undefined)) return false;

        // 3. (Fatura de Energia removed — checked at UC level now)

        // 4. Documentos Assinante
        if (!docs.rg_cnh?.presenca) return false;
        if (docs.rg_cnh?.presenca === 'Completo') {
            if (docs.rg_cnh?.ativo === undefined) return false;
            const doc = docs.rg_cnh as any;
            if (doc.legivel === undefined) return false;
        }

        if (!docs.procuracao?.presenca) return false;
        if (docs.procuracao?.presenca === 'Completo') {
            const p = docs.procuracao as any;
            if (!p.nome_procurador || !p.cpf_procurador || !p.validade_procuracao || !p.poderes_especificos) return false;
            if (p.legivel === undefined || p.atual === undefined) return false;
        }

        // Cartão CNPJ
        if (formData.tipo_cliente === 'PJ' || isCondominio) {
            if (docs.cartao_cnpj?.apresentado === undefined) return false;
            if (docs.cartao_cnpj?.apresentado === true && !docs.cartao_cnpj?.status) return false;
        }

        // Checklist Específico
        if (isCondominio) {
            if (!docs.ata_constituicao?.presenca) return false;
            if (docs.ata_constituicao?.presenca === 'Completo') {
                const ac = docs.ata_constituicao as any;
                if (!ac.data_constituicao || !ac.cnpj_condominio_str) return false;
                if (ac.legivel === undefined) return false;
            }
            if (!docs.ata_eleicao?.presenca) return false;
            if (docs.ata_eleicao?.presenca === 'Completo') {
                const ae = docs.ata_eleicao as any;
                if (!ae.nome_representante || !ae.cpf_representante || !ae.validade_ata) return false;
                if (ae.legivel === undefined || ae.atual === undefined) return false;
            }
        }

        if (formData.tipo_cliente === 'PJ' && !isCondominio) {
            if (!docs.contrato_social?.presenca) return false;
            if (docs.contrato_social?.presenca === 'Completo') {
                const cs = docs.contrato_social as any;
                if (cs.cnpj_ativo === undefined || !cs.nome_socio_administrador || !cs.cpf_socio_administrador) return false;
                if (cs.legivel === undefined) return false;
            }
        }

        return true;
    }, [formData, docs, isCondominio]);

    React.useEffect(() => {
        if (onValidate) {
            onValidate(isValid);
        }
    }, [isValid, onValidate]);

    return (
        <div className="space-y-6 animate-slide-up">
            {/* ── Identificação Geral Form ── */}
            <div className="flex justify-between items-center sm:flex-row flex-col sm:items-end gap-4">
                <div>
                    <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                        <UserCheck size={20} className="text-primary" />
                        Identificação Geral
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 font-medium mt-1">Verifique os dados do cliente. Campos de identificação estão travados.</p>
                </div>
                <button onClick={handleSave} disabled={saving} className="group flex items-center px-5 py-2.5 bg-gradient-to-r from-primary to-primary/90 text-white text-sm font-bold rounded-xl hover:from-primary/95 hover:to-primary/85 disabled:opacity-50 transition-all duration-200 shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-[1px] active:translate-y-0 active:scale-[0.98]">
                    {saving ? (<><div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white mr-2"></div>Salvando...</>) : (<><Save className="w-4 h-4 mr-2 transition-transform group-hover:scale-110" />Salvar</>)}
                </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white dark:bg-slate-900/60 p-6 rounded-2xl border border-slate-200/80 dark:border-slate-800 mt-4 shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-2"><User className="w-3.5 h-3.5" />Nome / Razão Social</label>
                    <input type="text" value={formData.cliente_nome} disabled className={disabledInputClass} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-2"><Building className="w-3.5 h-3.5" />Tipo de Cliente</label>
                        <select value={formData.tipo_cliente} disabled className={disabledInputClass}>
                            <option value="PF">Pessoa Física</option>
                            <option value="PJ">Pessoa Jurídica</option>
                        </select>
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-2"><Landmark className="w-3.5 h-3.5" />CPF / CNPJ</label>
                        <input type="text" value={formData.cpf_cnpj} disabled className={disabledInputClass} />
                    </div>
                </div>
                <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-2"><Phone className="w-3.5 h-3.5" />Telefone / WhatsApp</label>
                    <input type="tel" name="whatsapp" value={formData.whatsapp} onChange={handleChange} className={inputClass} placeholder="(00) 00000-0000" />
                </div>
                <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-2"><Mail className="w-3.5 h-3.5" />E-mail do Cliente</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} className={inputClass} placeholder="cliente@email.com" />
                </div>
                <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-2"><User className="w-3.5 h-3.5" />Contato Financeiro</label>
                    <input type="text" name="contato_financeiro" value={formData.contato_financeiro} onChange={handleChange} className={inputClass} placeholder="Vazio: o próprio cliente" />
                </div>
                <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-2"><Mail className="w-3.5 h-3.5" />E-mail Financeiro</label>
                    <input type="email" name="email_financeiro" value={formData.email_financeiro} onChange={handleChange} className={inputClass} placeholder="Vazio: o e-mail do cliente" />
                </div>
                <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-2"><Phone className="w-3.5 h-3.5" />WhatsApp Financeiro</label>
                    <input type="tel" name="whatsapp_financeiro" value={formData.whatsapp_financeiro} onChange={handleChange} className={inputClass} placeholder="Vazio: o WhatsApp do cliente" />
                </div>
                {formData.tipo_cliente === 'PJ' && (
                    <div className="space-y-2 md:col-span-2">
                        <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-2"><User className="w-3.5 h-3.5" />Representante/Responsável Legal (Para CNPJ)</label>
                        <input type="text" name="nome_representante_legal" value={formData.nome_representante_legal} onChange={handleChange} className={inputClass} placeholder="Nome do representante legal" />
                    </div>
                )}
            </div>

            {/* ── Notas da Negociação (only if notes exist) ── */}
            {!loadingNotas && notas.length > 0 && (
                <div className="bg-white dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden">
                    <button
                        type="button"
                        onClick={() => setShowNotas(!showNotas)}
                        className="w-full flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors"
                    >
                        <div className="flex items-center gap-2.5">
                            <div className="p-1.5 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                                <StickyNote size={14} className="text-amber-600 dark:text-amber-400" />
                            </div>
                            <h4 className="font-bold text-sm text-slate-700 dark:text-slate-200">Notas da Negociação</h4>
                            <span className="px-2 py-0.5 text-[10px] font-bold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full">{notas.length}</span>
                        </div>
                        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-300 ${showNotas ? 'rotate-180' : ''}`} />
                    </button>
                    {showNotas && (
                        <div className="p-5 space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
                            {notas.map((nota) => {
                                const dateStr = new Date(nota.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
                                const authorName = nota.user?.full_name || 'Usuário';
                                const tipoLabel = nota.tipo === 'whatsapp' ? '💬 WhatsApp' : nota.tipo === 'email' ? '📧 E-mail' : '📝 Nota';
                                return (
                                    <div key={nota.id} className="flex gap-3 p-3.5 bg-slate-50/80 dark:bg-slate-800/30 rounded-xl border border-slate-100 dark:border-slate-800 hover:border-slate-200 dark:hover:border-slate-700 transition-all">
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 flex items-center justify-center shrink-0 text-[11px] font-black text-primary">
                                            {authorName.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-xs font-bold text-slate-700 dark:text-slate-200 truncate">{authorName}</span>
                                                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{tipoLabel}</span>
                                                <span className="text-[9px] text-slate-400 ml-auto whitespace-nowrap">{dateStr}</span>
                                            </div>
                                            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-wrap break-words line-clamp-3">{nota.conteudo}</p>
                                            {nota.arquivo_url && (
                                                <a href={nota.arquivo_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-1.5 text-[10px] font-bold text-primary hover:underline">
                                                    <FileText size={11} /> {nota.arquivo_nome || 'Anexo'} <ExternalLink size={9} />
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ── Conferência Documental (merged) ── */}
            <div className="flex justify-between items-center sm:flex-row flex-col sm:items-end gap-4">
                <div>
                    <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                        <ClipboardCheck size={20} className="text-primary" />
                        Conferência Documental
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 font-medium mt-1">Marque a presença dos documentos para exibir análises adicionais de integridade.</p>
                </div>
            </div>

            {/* ── Documentos da Negociação (top) ── */}
            <div className="bg-white dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden">
                <div className="flex items-center gap-2.5 px-6 py-4 border-b border-slate-100 dark:border-slate-800">
                    <div className="p-1.5 bg-primary/8 dark:bg-primary/15 rounded-lg">
                        <FileText size={14} className="text-primary" />
                    </div>
                    <h4 className="font-bold text-sm text-slate-700 dark:text-slate-200">Documentos da Negociação</h4>
                </div>
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {/* Nº do Termo como doc card */}
                        {termoNumero && (
                            termoLink ? (
                                <a href={termoLink} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-3 rounded-lg border border-primary/20 bg-primary/5 dark:bg-primary/10 hover:border-primary/40 hover:bg-primary/10 transition-all group">
                                    <div className="flex items-center gap-3 overflow-hidden">
                                        <div className="p-2 rounded bg-white dark:bg-slate-800 text-primary transition-colors"><Link2 size={18} /></div>
                                        <div className="flex flex-col overflow-hidden leading-tight">
                                            <span className="text-xs font-bold text-primary truncate">Termo Nº {termoNumero}</span>
                                            {/* O analista precisa saber, antes de abrir, se este é o termo que o
                                                cliente assinou — quando há mais de um termo emitido, abrir o
                                                errado é indistinguível de abrir o certo. */}
                                            <span className={`text-[10px] font-semibold mt-0.5 ${termoAssinadoEm ? 'text-primary/70' : 'text-amber-600 dark:text-amber-400'}`}>
                                                {termoAssinadoEm
                                                    ? `Assinado em ${new Date(termoAssinadoEm).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                                                    : 'Sem assinatura registrada'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity"><ExternalLink size={14} className="text-primary" /></div>
                                </a>
                            ) : (
                                <div className="flex items-center p-3 rounded-lg border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30">
                                    <div className="flex items-center gap-3 overflow-hidden">
                                        <div className="p-2 rounded bg-white dark:bg-slate-800 text-slate-400 dark:text-slate-500"><Link2 size={18} /></div>
                                        <span className="text-xs font-bold text-slate-600 dark:text-slate-300 truncate">Termo Nº {termoNumero}</span>
                                    </div>
                                </div>
                            )
                        )}
                        {/* Simulação anexada ao termo (Plano de Benefícios do Quadro Resumo) */}
                        {simulacaoDoTermo && (
                            simulacaoDoTermo.link ? (
                                <a href={simulacaoDoTermo.link} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-3 rounded-lg border border-primary/20 bg-primary/5 dark:bg-primary/10 hover:border-primary/40 hover:bg-primary/10 transition-all group">
                                    <div className="flex items-center gap-3 overflow-hidden">
                                        <div className="p-2 rounded bg-white dark:bg-slate-800 text-primary transition-colors"><Zap size={18} /></div>
                                        <div className="flex flex-col overflow-hidden leading-tight">
                                            <span className="text-xs font-bold text-primary truncate">{simulacaoDoTermo.numero ? `Simulação ${simulacaoDoTermo.numero}` : `Simulação${simulacaoDataStr ? ` de ${simulacaoDataStr}` : ''}`}</span>
                                            <span className="text-[10px] font-semibold text-primary/70 mt-0.5">Anexada ao termo{simulacaoDoTermo.numero && simulacaoDataStr ? ` · ${simulacaoDataStr}` : ''}</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity"><ExternalLink size={14} className="text-primary" /></div>
                                </a>
                            ) : (
                                <div className="flex items-center p-3 rounded-lg border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30">
                                    <div className="flex items-center gap-3 overflow-hidden">
                                        <div className="p-2 rounded bg-white dark:bg-slate-800 text-slate-400 dark:text-slate-500"><Zap size={18} /></div>
                                        <div className="flex flex-col overflow-hidden leading-tight">
                                            <span className="text-xs font-bold text-slate-600 dark:text-slate-300 truncate">{simulacaoDoTermo.numero ? `Simulação ${simulacaoDoTermo.numero}` : `Simulação${simulacaoDataStr ? ` de ${simulacaoDataStr}` : ''}`}</span>
                                            <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 mt-0.5">Anexada ao termo · sem PDF</span>
                                        </div>
                                    </div>
                                </div>
                            )
                        )}
                        {/* Documentos do vendedor */}
                        {documentos_json && documentos_json.filter((doc: any) => doc.source !== 'analise').map((doc, index) => (
                            <div key={`seller-${index}`} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 hover:border-primary/30 hover:bg-primary/5 transition-all group">
                                <a href={doc.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 overflow-hidden flex-1">
                                    <div className="p-2 rounded bg-white dark:bg-slate-800 text-slate-400 dark:text-slate-500 group-hover:text-primary transition-colors"><FileText size={18} /></div>
                                    <span className="text-xs font-bold text-slate-600 dark:text-slate-300 truncate group-hover:text-slate-900 dark:group-hover:text-white">{doc.name}</span>
                                </a>
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <a href={doc.url} target="_blank" rel="noopener noreferrer" className="p-1.5 text-primary hover:bg-primary/10 rounded-lg transition-all">
                                        <ExternalLink size={14} className="text-primary" />
                                    </a>
                                    <button onClick={() => handleDeleteDocument(doc.url)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all">
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        ))}
                        {/* Documentos Anexados na Análise */}
                        {documentos_json && documentos_json.filter((doc: any) => doc.source === 'analise').map((doc, index) => (
                            <div key={`analise-${index}`} className="flex items-center justify-between p-3 rounded-lg border border-emerald-100 dark:border-emerald-900/40 bg-emerald-50/60 dark:bg-emerald-900/20 hover:border-emerald-300 dark:hover:border-emerald-800 transition-all group relative">
                                <a href={doc.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 overflow-hidden flex-1">
                                    <div className="p-2 rounded bg-white dark:bg-slate-800 text-emerald-500 transition-colors"><FileText size={18} /></div>
                                    <div className="flex flex-col overflow-hidden leading-tight">
                                        <span className="text-xs font-bold text-emerald-700 dark:text-emerald-400 truncate group-hover:text-emerald-800 dark:group-hover:text-emerald-300">{doc.name}</span>
                                        <span className="text-[10px] font-semibold text-emerald-600/70 dark:text-emerald-500/70 mt-0.5">Anexado na Análise</span>
                                    </div>
                                </a>
                                <div className="flex gap-1 items-center">
                                    <a href={doc.url} target="_blank" rel="noopener noreferrer" className="p-1.5 text-emerald-600 dark:text-emerald-400 opacity-0 group-hover:opacity-100 hover:text-emerald-700 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 rounded-lg transition-all">
                                        <ExternalLink size={16} />
                                    </a>
                                    <button onClick={() => handleDeleteDocument(doc.url)} className="p-1.5 text-red-400 opacity-0 group-hover:opacity-100 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all">
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        ))}
                        {/* Pasta do SharePoint */}
                        {negociacao.pasta_sharepoint_url && (
                            <a href={negociacao.pasta_sharepoint_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-3 rounded-lg border border-primary/20 bg-primary/5 dark:bg-primary/10 hover:border-primary/40 hover:bg-primary/10 transition-all group">
                                <div className="flex items-center gap-3 overflow-hidden">
                                    <div className="p-2 rounded bg-white dark:bg-slate-800 text-primary transition-colors"><ExternalLink size={18} /></div>
                                    <span className="text-xs font-bold text-primary truncate">Pasta da Negociação (Docs)</span>
                                </div>
                                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity"><ExternalLink size={14} className="text-primary" /></div>
                            </a>
                        )}
                        {/* Drag and Drop para adicionar novos */}
                        <div
                            onDrop={handleDrop}
                            onDragOver={(e) => e.preventDefault()}
                            onClick={() => fileInputRef.current?.click()}
                            className="flex flex-col items-center justify-center p-3 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-primary/50 dark:hover:border-primary/50 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer relative"
                        >
                            {uploadingFiles ? (
                                <div className="flex flex-col items-center gap-1 text-primary">
                                    <Loader2 size={18} className="animate-spin" />
                                    <span className="text-[10px] font-bold">Enviando...</span>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-1 text-slate-500 dark:text-slate-400">
                                    <Upload size={18} />
                                    <span className="text-[10px] font-semibold text-center mt-1">Arraste ou clique<br />para anexar documento</span>
                                </div>
                            )}
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileUpload}
                                className="hidden"
                                multiple
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Condomínio toggle - Apenas para PJ */}
            {formData.tipo_cliente === 'PJ' && (
                <div className={`p-5 rounded-2xl border transition-all duration-300 flex justify-between items-center ${isCondominio ? 'border-amber-400/50 bg-amber-50/80 dark:bg-amber-900/10 shadow-md shadow-amber-500/5' : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 hover:border-slate-300 dark:hover:border-slate-700 shadow-sm'}`}>
                    <div>
                        <div className="font-bold text-sm text-slate-800 dark:text-slate-200">É Condomínio?</div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Caso seja, o checklist será ajustado com documentos específicos.</p>
                    </div>
                    <div className="flex gap-4">
                        <label className="inline-flex items-center gap-2 cursor-pointer bg-slate-50 dark:bg-slate-900/80 px-4 py-2.5 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm hover:border-primary/50 hover:bg-white dark:hover:bg-slate-900 transition-colors">
                            <input type="radio" checked={isCondominio === true} onChange={() => onAnaliseChange({ is_condominio: true })} className="w-4 h-4 text-primary focus:ring-primary border-slate-700 bg-slate-800/60" />
                            <span className="text-sm font-bold text-slate-700 dark:text-slate-200">Sim</span>
                        </label>
                        <label className="inline-flex items-center gap-2 cursor-pointer bg-slate-50 dark:bg-slate-900/80 px-4 py-2.5 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm hover:border-primary/50 hover:bg-white dark:hover:bg-slate-900 transition-colors">
                            <input type="radio" checked={isCondominio === false} onChange={() => onAnaliseChange({ is_condominio: false })} className="w-4 h-4 text-primary focus:ring-primary border-slate-700 bg-slate-800/60" />
                            <span className="text-sm font-bold text-slate-700 dark:text-slate-200">Não</span>
                        </label>
                    </div>
                </div>
            )}

            {/* ── Accordion Checklist ── */}
            <AccordionChecklist
                docs={docs}
                updateTermo={updateTermo}
                updateDoc={updateDoc}
                updateGD={updateGD}
                renderDocRowExpanded={renderDocRowExpanded}
                renderFaturaEnergiaExpanded={renderFaturaEnergiaExpanded}
                renderCartaoCNPJ={renderCartaoCNPJ}
                formData={formData}
                isCondominio={isCondominio}
                analiseData={analiseData}
                onAnaliseChange={onAnaliseChange}
            />

            {/* Notificar Vendedor - outside AccordionChecklist so it has access to state */}
            <div className="bg-white dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden">
                <div className="p-5">
                    <div className="p-4 bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800/40 rounded-xl">
                        <div className="flex items-center gap-2 mb-3">
                            <AlertTriangle size={16} className="text-orange-600" />
                            <span className="text-sm font-bold text-orange-800 dark:text-orange-400">Notificar Vendedor sobre Pendência Documental</span>
                        </div>
                        <textarea
                            value={pendenciaMsg}
                            onChange={(e) => setPendenciaMsg(e.target.value)}
                            rows={2}
                            className="w-full rounded-lg border border-orange-200 dark:border-orange-700/50 bg-white dark:bg-slate-800/60 px-3 py-2 text-sm text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none resize-none placeholder:text-orange-300"
                            placeholder="Descreva a pendência que o vendedor precisa resolver em até 24h..."
                        />
                        <button
                            disabled={!pendenciaMsg.trim() || sendingPendencia}
                            onClick={async () => {
                                if (!pendenciaMsg.trim()) return;
                                setSendingPendencia(true);
                                try {
                                    await supabase.from('negociacao_logs').insert({
                                        negociacao_id: negociacao.id,
                                        acao: `[PENDENCIA_DOCUMENTAL] ${pendenciaMsg}`,
                                        detalhes: `Analista ${profile?.full_name || 'Analista'} identificou pendência documental no Step 1 (Identificação).`,
                                        user_id: user?.id
                                    });
                                    const sellerId = negociacao.user_id || (negociacao as any).responsavel_id;
                                    if (sellerId) {
                                        await supabase.from('notifications').insert({
                                            user_id: sellerId,
                                            type: 'pendencia_documental',
                                            title: 'Pendência Documental - Ação Necessária',
                                            content: `Pendência na negociação "${negociacao.cliente_nome}": ${pendenciaMsg}. Prazo: 24h.`,
                                            link: `/crm?negociacaoId=${negociacao.id}`,
                                            read: false
                                        });
                                    }
                                    addToast('Pendência enviada ao vendedor!', 'success');
                                    logUsageEvent({ userId: user?.id, modulo: 'gestao', funcionalidade: 'dashboard', acao: 'enviar_pendencia_documental', origem: 'acao' });
                                    setPendenciaMsg('');
                                } catch (err: any) {
                                    addToast('Erro ao enviar: ' + err.message, 'error');
                                } finally {
                                    setSendingPendencia(false);
                                }
                            }}
                            className="mt-2 flex items-center gap-2 px-4 py-2 bg-orange-600 text-white text-xs font-bold rounded-lg hover:bg-orange-700 transition-all shadow-sm disabled:opacity-50"
                        >
                            <Send size={14} /> {sendingPendencia ? 'Enviando...' : 'Enviar Pendência ao Vendedor'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ════════════════════════════════════════════════════════ */
/*  AccordionChecklist — 3 seções colapsáveis               */
/* ════════════════════════════════════════════════════════ */

interface AccordionChecklistProps {
    docs: ConferenciaDocs;
    updateTermo: (field: string, value: any) => void;
    updateDoc: (key: keyof ConferenciaDocs, field: string, value: any) => void;
    updateGD: (value: boolean) => void;
    renderDocRowExpanded: (title: string, docKey: keyof ConferenciaDocs, showNA?: boolean, hasAtual?: boolean, hasDivergente?: boolean, isOnlyStatus?: boolean, customContent?: React.ReactNode) => React.ReactNode;
    renderFaturaEnergiaExpanded: () => React.ReactNode;
    renderCartaoCNPJ: () => React.ReactNode;
    formData: { tipo_cliente: string };
    isCondominio: boolean;
    analiseData: Partial<AnaliseCadastral>;
    onAnaliseChange: (data: Partial<AnaliseCadastral>) => void;
}

function AccordionChecklist({ docs, updateTermo, updateDoc, updateGD, renderDocRowExpanded, renderFaturaEnergiaExpanded, renderCartaoCNPJ, formData, isCondominio, analiseData, onAnaliseChange }: AccordionChecklistProps) {
    const [openSections, setOpenSections] = useState<Record<string, boolean>>({ termo: true, docs_assinante: false });

    const toggle = (key: string) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

    // ── Termo verification questions ──
    const addressKeys = ['endereco_rua', 'endereco_numero', 'endereco_quadra_lote', 'endereco_bairro', 'endereco_cidade', 'endereco_cep'];
    const termoFields: { key: string; label: string }[] = [
        { key: 'assinante_titular_poderes', label: 'Assinante é titular e tem poderes para assinar?' },
        { key: 'dados_associado_corretos', label: 'Dados do associado estão preenchidos corretamente conforme fatura/docs?' },
        { key: 'desconto_fidelidade_corretos', label: 'As informações do desconto e fidelidade estão corretamente preenchidas?' },
        { key: 'ucs_conferem_faturas', label: 'As informações das unidades (UC) relacionadas conferem com as Faturas?' },
        { key: 'todas_ucs_titular', label: 'Todas as unidades são do titular assinante?' },
        { key: 'termo_assinado', label: 'Termo assinado?' },
        { key: 'autenticidade_assinaturas', label: 'Autenticidade das assinaturas?' },
        { key: 'desconto_concedido_certo', label: 'O desconto concedido está certo?' },
        { key: 'quantidade_unidades_certa', label: 'A quantidade de unidades está certa?' },
    ];

    const totalTermoCount = termoFields.length + addressKeys.length;
    const termoValidatedCount = termoFields.filter(f => docs.termo_adesao?.[f.key as keyof NonNullable<ConferenciaDocs['termo_adesao']>] !== undefined).length
        + addressKeys.filter(k => docs.termo_adesao?.[k as keyof NonNullable<ConferenciaDocs['termo_adesao']>] !== undefined).length;

    // ── Docs do Assinante count ──
    const isRgValid = () => {
        const d = docs.rg_cnh as any;
        if (!d?.presenca) return false;
        if (d.presenca === 'Completo') return d.ativo !== undefined && d.legivel !== undefined;
        return true;
    };

    const isProcuracaoValid = () => {
        const d = docs.procuracao as any;
        if (!d?.presenca) return false;
        if (d.presenca === 'Completo') return d.legivel !== undefined && d.atual !== undefined && !!d.nome_procurador && !!d.cpf_procurador && !!d.validade_procuracao && !!d.poderes_especificos;
        return true;
    };

    const isContratoValid = () => {
        const d = docs.contrato_social as any;
        if (!d?.presenca) return false;
        if (d.presenca === 'Completo') return d.cnpj_ativo !== undefined && !!d.nome_socio_administrador && !!d.cpf_socio_administrador && d.legivel !== undefined;
        return true;
    };

    const isAtaConstituicaoValid = () => {
        const d = docs.ata_constituicao as any;
        if (!d?.presenca) return false;
        if (d.presenca === 'Completo') return !!d.data_constituicao && !!d.cnpj_condominio_str && d.legivel !== undefined;
        return true;
    };

    const isAtaEleicaoValid = () => {
        const d = docs.ata_eleicao as any;
        if (!d?.presenca) return false;
        if (d.presenca === 'Completo') return !!d.nome_representante && !!d.cpf_representante && !!d.validade_ata && d.legivel !== undefined && d.atual !== undefined;
        return true;
    };

    const docAssinanteItems = [
        { done: isRgValid() },
        { done: isProcuracaoValid() }
    ];
    if (isCondominio) {
        docAssinanteItems.push({ done: isAtaConstituicaoValid() });
        docAssinanteItems.push({ done: isAtaEleicaoValid() });
    } else if (formData.tipo_cliente === 'PJ') {
        docAssinanteItems.push({ done: isContratoValid() });
    }

    const docsAssinanteValidated = docAssinanteItems.filter(d => d.done).length;



    const renderTermoBoolInline = (fieldKey: string, label: string) => {
        const val = docs.termo_adesao?.[fieldKey as keyof NonNullable<ConferenciaDocs['termo_adesao']>];
        return (
            <div key={fieldKey} className="flex flex-col gap-1.5 items-center bg-slate-50 dark:bg-slate-900/50 p-2.5 px-3.5 rounded-xl border border-slate-200/80 dark:border-slate-700/80 shadow-sm flex-1 min-w-[110px] hover:border-primary/30 transition-colors">
                <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-tight text-center leading-tight line-clamp-2 h-auto flex-1 flex items-center">{label}</span>
                <div className="flex items-center gap-2 mt-auto">
                    <label className="inline-flex items-center gap-1 cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/40 px-2 py-1 rounded-md transition-colors">
                        <input type="radio" checked={val === true} onChange={() => updateTermo(fieldKey, val === true ? undefined : true)} className="w-3.5 h-3.5 text-green-500 focus:ring-green-500 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800" />
                        <span className="text-[10.5px] font-bold text-slate-700 dark:text-slate-200">Sim</span>
                    </label>
                    <label className="inline-flex items-center gap-1 cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/40 px-2 py-1 rounded-md transition-colors">
                        <input type="radio" checked={val === false} onChange={() => updateTermo(fieldKey, val === false ? undefined : false)} className="w-3.5 h-3.5 text-red-500 focus:ring-red-500 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800" />
                        <span className="text-[10.5px] font-bold text-slate-700 dark:text-slate-200">Não</span>
                    </label>
                </div>
            </div>
        );
    };

    // Variante com 3 opções: Sim / Não / Não existe — para campos opcionais de endereço
    const renderTermoBoolWithNA = (fieldKey: string, label: string) => {
        const val = docs.termo_adesao?.[fieldKey as keyof NonNullable<ConferenciaDocs['termo_adesao']>];
        return (
            <div key={fieldKey} className="flex flex-col gap-1.5 items-center bg-slate-50 dark:bg-slate-900/50 p-2.5 px-3.5 rounded-xl border border-slate-200/80 dark:border-slate-700/80 shadow-sm flex-1 min-w-[130px] hover:border-primary/30 transition-colors">
                <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-tight text-center leading-tight h-auto flex-1 flex items-center">{label}</span>
                <div className="flex flex-wrap items-center gap-1 mt-auto justify-center">
                    <label className="inline-flex items-center gap-1 cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/40 px-2 py-1 rounded-md transition-colors">
                        <input type="radio" checked={val === true} onChange={() => updateTermo(fieldKey, val === true ? undefined : true)} className="w-3.5 h-3.5 text-green-500 focus:ring-green-500 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800" />
                        <span className="text-[10.5px] font-bold text-slate-700 dark:text-slate-200">Sim</span>
                    </label>
                    <label className="inline-flex items-center gap-1 cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/40 px-2 py-1 rounded-md transition-colors">
                        <input type="radio" checked={val === false} onChange={() => updateTermo(fieldKey, val === false ? undefined : false)} className="w-3.5 h-3.5 text-red-500 focus:ring-red-500 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800" />
                        <span className="text-[10.5px] font-bold text-slate-700 dark:text-slate-200">Não</span>
                    </label>
                    <label className="inline-flex items-center gap-1 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700/60 px-2 py-1 rounded-md transition-colors">
                        <input type="radio" checked={val === 'nao_existe'} onChange={() => updateTermo(fieldKey, val === 'nao_existe' ? undefined : 'nao_existe')} className="w-3.5 h-3.5 text-slate-500 focus:ring-slate-400 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800" />
                        <span className="text-[10.5px] font-bold text-slate-500 dark:text-slate-400">N/A</span>
                    </label>
                </div>
            </div>
        );
    };

    const renderAddressChecklist = () => {
        const allChecked = addressKeys.every(k => docs.termo_adesao?.[k as keyof NonNullable<ConferenciaDocs['termo_adesao']>] !== undefined);
        const hasError = addressKeys.some(k => docs.termo_adesao?.[k as keyof NonNullable<ConferenciaDocs['termo_adesao']>] === false);

        return (
            <div className={`p-4 rounded-xl border transition-all duration-200 ${allChecked ? (hasError ? 'border-red-200 bg-red-50/40 dark:border-red-800/50 dark:bg-red-900/10' : 'border-green-200 bg-green-50/60 dark:border-green-800/50 dark:bg-green-900/10') : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 hover:border-slate-300 dark:hover:border-slate-700'}`}>
                <div className="font-bold text-sm text-slate-800 dark:text-slate-200 mb-3 flex items-center justify-between">
                    <span>Endereço Completo Confere?</span>
                    <span className="text-[10px] bg-slate-100 dark:bg-slate-800/80 text-slate-500 dark:text-slate-400 px-2 py-1 rounded font-semibold uppercase tracking-wider">Verificação de Endereço</span>
                </div>
                <div className="flex flex-wrap gap-2 sm:gap-3">
                    {renderTermoBoolInline('endereco_rua', 'Rua')}
                    {renderTermoBoolWithNA('endereco_numero', 'Núm.')}
                    {renderTermoBoolWithNA('endereco_quadra_lote', 'Quadra / Lote')}
                    {renderTermoBoolInline('endereco_bairro', 'Bairro')}
                    {renderTermoBoolInline('endereco_cidade', 'Cidade')}
                    {renderTermoBoolInline('endereco_cep', 'CEP')}
                </div>
            </div>
        );
    };

    const renderBoolToggle = (fieldKey: string, label: string) => {
        const val = docs.termo_adesao?.[fieldKey as keyof NonNullable<ConferenciaDocs['termo_adesao']>];
        return (
            <div key={fieldKey} className={`flex items-center justify-between p-3.5 rounded-xl border transition-all duration-200 ${val === true ? 'border-green-200 dark:border-green-800/50 bg-green-50/60 dark:bg-green-900/10' :
                val === false ? 'border-red-200 dark:border-red-800/50 bg-red-50/40 dark:bg-red-900/10' :
                    'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 hover:border-slate-300 dark:hover:border-slate-700'
                }`}>
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300 pr-4">{label}</span>
                <div className="flex items-center gap-3 shrink-0">
                    <label className="inline-flex items-center gap-1.5 cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/30 px-3 py-1.5 rounded-lg transition-colors">
                        <input type="radio" checked={val === true} onChange={() => updateTermo(fieldKey, val === true ? undefined : true)} className="w-4 h-4 text-green-500 focus:ring-green-500 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800" />
                        <span className="text-xs font-bold text-slate-700 dark:text-slate-200">Sim</span>
                    </label>
                    <label className="inline-flex items-center gap-1.5 cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/30 px-3 py-1.5 rounded-lg transition-colors">
                        <input type="radio" checked={val === false} onChange={() => updateTermo(fieldKey, val === false ? undefined : false)} className="w-4 h-4 text-red-500 focus:ring-red-500 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800" />
                        <span className="text-xs font-bold text-slate-700 dark:text-slate-200">Não</span>
                    </label>
                </div>
            </div>
        );
    };

    const sectionHeader = (key: string, icon: React.ReactNode, title: string, subtitle: string, validated: number, total: number) => (
        <button
            type="button"
            onClick={() => toggle(key)}
            className="w-full flex items-center justify-between p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900/60 hover:border-primary/30 dark:hover:border-primary/20 hover:shadow-md transition-all duration-300 shadow-sm group"
        >
            <div className="flex items-center gap-4">
                <div className={`p-2.5 rounded-xl transition-all duration-300 ${validated === total && total > 0 ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 shadow-sm shadow-green-100' :
                    validated > 0 ? 'bg-primary/8 dark:bg-primary/15 text-primary shadow-sm shadow-primary/5' :
                        'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 group-hover:text-primary group-hover:bg-primary/8'
                    }`}>
                    {icon}
                </div>
                <div className="text-left">
                    <h4 className="font-bold text-sm text-slate-800 dark:text-slate-200 group-hover:text-primary transition-colors">{title}</h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 font-medium">{subtitle}</p>
                </div>
            </div>
            <div className="flex items-center gap-3">
                <span className={`px-3 py-1.5 rounded-full text-[11px] font-bold transition-all duration-300 ${validated === total && total > 0 ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400 border border-green-200/80 dark:border-green-800/40' :
                    validated > 0 ? 'bg-primary/8 text-primary dark:bg-primary/15 border border-primary/20' :
                        'bg-slate-50 text-slate-500 dark:bg-slate-800 dark:text-slate-400 border border-slate-200/80 dark:border-slate-700/80'
                    }`}>
                    {validated}/{total}
                </span>
                <ChevronDown className={`w-5 h-5 text-slate-400 group-hover:text-primary transition-all duration-300 ${openSections[key] ? 'rotate-180' : ''}`} />
            </div>
        </button>
    );

    return (
        <div className="space-y-3">
            {/* ═══ 1. TERMO DE ADESÃO ═══ */}
            {sectionHeader('termo', <ClipboardCheck size={20} />, 'Termo de Adesão', `Contém ${totalTermoCount} verificações para validação.`, termoValidatedCount, totalTermoCount)}
            {openSections.termo && (
                <div className="space-y-3 pl-2 pr-1 animate-in fade-in slide-in-from-top-2 duration-300">
                    {renderAddressChecklist()}
                    <div className="space-y-2 pt-1">
                        {termoFields.map(f => renderBoolToggle(f.key, f.label))}
                    </div>
                </div>
            )}

            {/* ═══ 2. DOCUMENTOS DO ASSINANTE ═══ */}
            {sectionHeader('docs_assinante', <UserCheck size={20} />, 'Documentos do Assinante', `Contém ${docAssinanteItems.length} documentos para verificação.`, docsAssinanteValidated, docAssinanteItems.length)}
            {openSections.docs_assinante && (
                <div className="space-y-3 pl-2 pr-1 animate-in fade-in slide-in-from-top-2 duration-300">
                    {renderDocRowExpanded('RG + CPF / Doc. Representante (Frente e Verso)', 'rg_cnh')}
                    {renderDocRowExpanded('Procuração (Se aplicável)', 'procuracao', true, true, true)}
                    {isCondominio && (
                        <>
                            {renderDocRowExpanded('Estatuto / Ata de Constituição', 'ata_constituicao')}
                            {renderDocRowExpanded('Ata de Eleição do Representante', 'ata_eleicao', false, true, true)}
                        </>
                    )}
                    {formData.tipo_cliente === 'PJ' && !isCondominio && (
                        renderDocRowExpanded('Contrato Social + Última Alteração', 'contrato_social')
                    )}
                </div>
            )}



            {/* ── Pendências ── */}
            <div className="bg-white dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden">
                <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
                    <div className="p-1.5 bg-primary/8 dark:bg-primary/15 rounded-lg">
                        <FileText size={14} className="text-primary" />
                    </div>
                    <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200">Pendências e Observações</h4>
                </div>
                <div className="p-5">
                    <textarea
                        name="pendencias_obs_docs"
                        value={analiseData.pendencias_obs_docs || ''}
                        onChange={(e) => onAnaliseChange({ pendencias_obs_docs: e.target.value })}
                        rows={4}
                        className="w-full rounded-xl border border-slate-200 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-800/40 px-4 py-3 text-sm text-slate-900 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40 resize-none shadow-sm transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500 font-medium hover:border-slate-300 dark:hover:border-slate-600"
                        placeholder="Digite aqui quaisquer pendências, anotações ou observações relevantes sobre a documentação analisada..."
                    />
                </div>
            </div>
        </div>
    );
}
